'use strict'

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

import Box from '@components/kit/box'
import Loading from '@components/kit/loading'
import ScrollView from '@components/kit/scroll-view'
import { ChatMessage } from '@store/Chat/types'
import { isSameCalendarDayInOffset } from '@utils/date'
import RoomMessage from './room-message'

interface RoomMessagesProps {
  userId: number
  messages: ChatMessage[]
  hasNextPage: boolean
  isLoading: boolean
  shouldBeSendingMessages?: boolean
  memberTimezoneOffset?: number
  memberDisplayName?: string
  onLoadMore: () => Promise<void>
  onGetDocumentUrl: (documentId: number) => Promise<string | null>
}

const shouldShowDateBreak = (
  messages: ChatMessage[],
  index: number,
  memberTimezoneOffset?: number
) => {
  const current = messages[index]
  if (!current.timestamp) return index === 0
  if (index === 0) return true
  const previous = messages[index - 1]
  return !isSameCalendarDayInOffset(
    current.timestamp,
    previous.timestamp,
    memberTimezoneOffset
  )
}

const shouldShowMemberAvatar = (messages: ChatMessage[], index: number) => {
  const current = messages[index]
  if (current.isSender) return false
  const next = messages[index + 1]
  return !next || next.isSender || next.ownerUserId !== current.ownerUserId
}

const shouldShowWalrusAIAvatar = (messages: ChatMessage[], index: number) => {
  const current = messages[index]
  if (!current.walrus_ai || !current.isSender) return false
  const next = messages[index + 1]
  return !next || !next.walrus_ai || !next.isSender
}

function RoomMessages({
  userId,
  messages,
  hasNextPage,
  isLoading,
  shouldBeSendingMessages,
  memberTimezoneOffset,
  memberDisplayName,
  onLoadMore,
  onGetDocumentUrl
}: RoomMessagesProps) {
  const scrollViewRef = useRef<React.ElementRef<typeof ScrollView>>(null)
  const messagesRef = useRef(messages)
  const prevLastIdRef = useRef<string | undefined>(undefined)
  const pendingPrependRef = useRef<{
    scrollHeight: number
    scrollTop: number
  } | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isListVisible, setIsListVisible] = useState(false)
  const isLoadingMoreRef = useRef(false)

  messagesRef.current = messages

  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore
  }, [isLoadingMore])

  const getNode = () => {
    return (
      (scrollViewRef.current?.getScrollableNode?.() as HTMLElement) ?? undefined
    )
  }

  const scrollToEnd = (animated = false) => {
    scrollViewRef.current?.scrollToEnd?.({ animated })

    const node = getNode()
    if (node) node.scrollTop = node.scrollHeight
  }

  const scrollToEndWithRetries = (animated = false) => {
    for (let i = 0; i < 10; i++) {
      setTimeout(() => scrollToEnd(animated), 100 + i * 100)
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToEnd(false)
      })
    })
  }

  const onScroll: NonNullable<
    React.ComponentProps<typeof ScrollView>['onScroll']
  > = async ({ nativeEvent: { contentOffset } }) => {
    if (contentOffset.y > 1 || !hasNextPage || isLoadingMore || isLoading)
      return

    const firstIdBefore = messagesRef.current[0]?.id
    const node = getNode()

    pendingPrependRef.current = node
      ? { scrollHeight: node.scrollHeight, scrollTop: node.scrollTop }
      : null

    setIsLoadingMore(true)
    await onLoadMore()
    setIsLoadingMore(false)

    if (
      pendingPrependRef.current &&
      firstIdBefore === messagesRef.current[0]?.id
    ) {
      pendingPrependRef.current = null
    }
  }

  const onContentSizeChange = () => {
    if (messagesRef.current.length > 0 && !isLoadingMoreRef.current) {
      setIsListVisible(true)
    }
  }

  useEffect(() => {
    setIsListVisible(false)
    prevLastIdRef.current = undefined
    pendingPrependRef.current = null
  }, [userId])

  useEffect(() => {
    scrollToEndWithRetries()
  }, [])

  useLayoutEffect(() => {
    if (messages.length === 0) return

    const node = getNode()
    const lastId = messages[messages.length - 1]?.id
    const prevLastId = prevLastIdRef.current

    if (pendingPrependRef.current) {
      if (!isLoadingMore && node) {
        const { scrollHeight, scrollTop } = pendingPrependRef.current

        // adjust magic number as needed - TODO: find a better way to handle this
        node.scrollTop = node.scrollHeight - scrollHeight + scrollTop - 46
        pendingPrependRef.current = null
      }
    } else if (lastId !== prevLastId) {
      if (prevLastId === undefined) {
        scrollToEnd(false)
      } else {
        scrollToEndWithRetries()
      }
    }

    prevLastIdRef.current = lastId

    if (messages.length > 0) {
      setIsListVisible(true)
    }
  }, [messages, isLoadingMore])

  if (isLoading && messages.length === 0) {
    return (
      <Box flex='1 1 0' justifyContent='center' alignItems='center'>
        <Loading />
      </Box>
    )
  }

  return (
    <ScrollView
      ref={scrollViewRef}
      flex='1 1 auto'
      opacity={isListVisible ? 1 : 0}
      px='16'
      style={{ flex: 1, minHeight: 0 }}
      onScroll={onScroll}
      onContentSizeChange={onContentSizeChange}
      scrollEventThrottle={2}
    >
      {isLoadingMore ? (
        <Box
          flex='1 1 auto'
          justifyContent='center'
          alignItems='center'
          pt='32'
          pb='24'
        >
          <Loading />
        </Box>
      ) : null}
      {messages.map((message, index) => (
        <RoomMessage
          key={message.id}
          message={message}
          userId={userId}
          showDateBreak={shouldShowDateBreak(
            messages,
            index,
            memberTimezoneOffset
          )}
          showMemberAvatar={shouldShowMemberAvatar(messages, index)}
          showWalrusAIAvatar={shouldShowWalrusAIAvatar(messages, index)}
          shouldBeSendingMessages={shouldBeSendingMessages}
          memberTimezoneOffset={memberTimezoneOffset}
          memberDisplayName={memberDisplayName}
          onGetDocumentUrl={onGetDocumentUrl}
        />
      ))}
    </ScrollView>
  )
}

export default RoomMessages
